import { useState, useEffect } from 'react';
import { auth, db } from '../lib/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { signIn as authSignIn, signOut as authSignOut } from '../lib/auth';

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAdmin = async (user: User) => {
      try {
        const adminDoc = await getDoc(doc(db, 'admins', user.uid));
        return adminDoc.exists();
      } catch (error) {
        console.error('Admin check error:', error);
        setError('Erreur lors de la vérification des droits administrateur');
        return false;
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        setUser(user);
        setIsAuthenticated(!!user);
        
        if (user) {
          const adminStatus = await checkAdmin(user);
          setIsAdmin(adminStatus);
        } else {
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Auth state change error:', error);
        setError('Erreur lors de la vérification de l\'authentification');
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    setError(null);
    try {
      const result = await authSignIn(email, password);
      if (result.error) {
        setError(result.error.message);
      }
      return result;
    } catch (error) {
      console.error('Sign in error:', error);
      setError('Une erreur inattendue est survenue');
      return {
        user: null,
        error: new Error('Une erreur inattendue est survenue')
      };
    }
  };

  const signOut = async () => {
    setError(null);
    try {
      await authSignOut();
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      setError('Une erreur est survenue lors de la déconnexion');
      return {
        error: new Error('Une erreur est survenue lors de la déconnexion')
      };
    }
  };

  return { 
    isAuthenticated, 
    isAdmin, 
    isLoading, 
    user,
    error,
    signIn,
    signOut
  };
};